import { Checkbox } from "@App/Components/common/Checkbox";
import type { PremiumService } from "@App/types";
import type { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

export type PremiumServiceStepProps = {
  availableSubservices: PremiumService[];
  premiumServices: PremiumService[];
  loading: boolean;
  toggled: (premiumService: PremiumService, premiumServices: PremiumService[]) => boolean;
  togglePremiumService: (premiumService: PremiumService, premiumServices: PremiumService[]) => void;
  nextStep: () => void;
};

/**
 * DHL's premium service step: the add-ons available for the chosen service.
 *
 * Picking one is optional, so unlike the shared `Partials/SubServiceStep` the continue button is
 * never disabled. It also owns its own loading state rather than letting a `<Loader />` sit in the
 * stepper as a child, where it would take up a step index and shift every step number after it.
 */
export const PremiumServiceStep: FunctionComponent<PremiumServiceStepProps> = ({
  availableSubservices,
  premiumServices,
  loading,
  toggled,
  togglePremiumService,
  nextStep,
}: PremiumServiceStepProps) => {
  const { t } = useTranslation();

  return (
    <div className="overflow-hidden mb-4 mt-4">
      {loading &&
        [0, 1, 2].map((placeholder) => (
          <div key={placeholder} className="animate-pulse flex items-center mb-2">
            <div className="w-8 h-8 bg-black-300 rounded shrink-0" />
            <div className="w-40 h-4 bg-black-300 rounded-md ml-4" />
          </div>
        ))}
      {!loading &&
        availableSubservices?.map((premiumService) => (
          <Checkbox
            key={premiumService.id}
            status={toggled(premiumService, premiumServices)}
            title={premiumService.name + (premiumService.paid ? "*" : "")}
            onChange={() => togglePremiumService(premiumService, premiumServices)}
          />
        ))}
      {!loading && (
        <button
          type="button"
          onClick={nextStep}
          className="bg-primary w-full md:w-80 px-8 py-2 mt-4 text-white rounded text-center font-serif font-bold cursor-pointer no-underline"
        >
          {t("weiter")}
        </button>
      )}
    </div>
  );
};
